/**
 * Centralized notification-routing helper for the frontend.
 *
 * Mirrors the backend `_resolve_notification_recipients` in
 * `/app/backend/server.py` so the UI can filter operator dropdowns
 * with the exact same policy used at write time. ANY change to the
 * routing rules MUST happen in both files; the regression tests
 * (`notificationRouting.test.mjs`) lock the role allow-list to keep
 * them in sync.
 *
 * Office rule (16/02/2026):
 *   Management Type "studio"  → notifica solo a:
 *     - amministratore
 *     - responsabile
 *     - operatore_studio
 *   Management Type "agenzia" → notifica solo a:
 *     - amministratore
 *     - responsabile
 *     - operatore_agenzia
 *
 * Amministratore + responsabile see BOTH (full-visibility role).
 */

const ROLE_COMPAT = {
  studio: new Set(["amministratore", "responsabile", "operatore_studio"]),
  agenzia: new Set(["amministratore", "responsabile", "operatore_agenzia"]),
};

/**
 * Normalize any raw input (practiceType "Gestionale Studio", areaType
 * "studio", managementType "agenzia", null/empty) into the canonical
 * short code "studio" | "agenzia". Defaults to "agenzia" when the
 * value is missing or unrecognised — matches the default operator role.
 */
export function normalizeManagementType(value) {
  if (!value) return "agenzia";
  const s = String(value).trim().toLowerCase();
  if (s.includes("studio")) return "studio";
  if (s.includes("agenzia") || s.includes("agency")) return "agenzia";
  return "agenzia";
}

/**
 * Return true when an operator with `role` should see notifications
 * tagged with the given management type. Amministratore and
 * responsabile always see both (full visibility).
 */
export function isRoleCompatibleWithManagementType(role, managementType) {
  if (!role) return false;
  const mt = normalizeManagementType(managementType);
  const r = String(role).trim().toLowerCase();
  return ROLE_COMPAT[mt].has(r);
}

/**
 * Filter a list of operators down to those compatible with the given
 * management type. Used by the Diario destinatario dropdown and any
 * future picker that needs management-type-aware operator selection.
 *
 * The input list shape is `{id, role, status, fullName, ...}`. We also
 * skip suspended (`status === "sospeso"`) operators because they cannot
 * act on a notification anyway.
 */
export function filterOperatorsByManagementType(operators, managementType) {
  if (!Array.isArray(operators)) return [];
  return operators.filter(
    (op) =>
      op &&
      op.status !== "sospeso" &&
      isRoleCompatibleWithManagementType(op.role, managementType)
  );
}
